<script lang="ts">
    import '../app.css';
    import theme from '#lib/actions/theme.ts';
    import { onMount, type Snippet } from 'svelte';
    import { browser } from '$app/env';
    import { page } from '$app/state';
    import {
        getCurrentUser,
        updateCurrentUser,
        clientChanged,
        userDeleted,
        userUpdated,
        gotoLogin
    } from '#api/auth.ts';
    import { setBaseUrl, setRequestOptions } from '@foundatiofx/fetchclient';
    import { authStatus, initAuthenticatedStores } from '#lib/stores/stores.ts';
    import { bindOperationsPollingToAuth } from '#lib/stores/operations.ts';
    import { getTabClientId, setTabClientId } from '#lib/utils/tabClientId.ts';
    import { setHubClient } from '#api/eventHub.ts';
    import { initTimeTravel } from '#lib/services/timeTravelSync.ts';
    import { showUnexpectedApiError } from '#lib/utils/apiToast.ts';
    import CookieBanner from '#comp/common/CookieBanner.svelte';
    import TopBanner from '#comp/common/TopBanner.svelte';
    import { Toaster } from '#comp/ui/sonner/index.ts';
    import type { LayoutData } from './$types';

    let { data, children }: { data: LayoutData; children: Snippet } = $props();

    // Clears the staff app's sticky FormFooter (~52px) so a toast never covers its submit button.
    const toastOffset = $derived(page.route.id?.startsWith('/(app)') ? { bottom: '4.5rem' } : undefined);

    type EventListener = (this: Document, ev: CustomEvent) => void;

    onMount(bindOperationsPollingToAuth);

    onMount(async () => {
        setBaseUrl('api');

        setRequestOptions({ errorCallback: showUnexpectedApiError });

        // The /impersonate route establishes auth itself (activate -> currentUser).
        // Running the bootstrap here races its activation POST: on the freshly
        // opened portal domain the cookie isn't set yet, so /auth/me returns null
        // and clobbers the just-activated user, leaving the portal logged out.
        if (page.url.pathname !== '/impersonate') {
            try {
                await updateCurrentUser();

                const user = getCurrentUser();
                authStatus.update(() => (user ? 'authenticated' : 'unauthenticated'));

                // getMe claims 401, so a dead session no longer redirects via the 401 middleware, and a
                // route load that already errored keeps ProtectedRoute from mounting to do it either.
                if (!user) await gotoLogin();

                if (user && !user.isObligor && getTabClientId() == null) {
                    setTabClientId(user.clientId);
                }

                if (user && !user.isObligor) setHubClient(getTabClientId());

                if (user) await initAuthenticatedStores();

                if (user) initTimeTravel();
            } catch {
                authStatus.update(() => 'unauthenticated');
                await gotoLogin();
            }
        }

        if (browser) {
            document.addEventListener(
                'ConcordServicing.Common.Events.UserUpdated',
                (detail) => userUpdated(detail),
                false
            );
            document.addEventListener('ConcordServicing.Common.Events.UserDeleted', userDeleted as EventListener);
            document.addEventListener('ConcordServicing.Common.Events.ClientCreated', clientChanged as EventListener);
            document.addEventListener('ConcordServicing.Common.Events.ClientUpdated', clientChanged as EventListener);
            document.addEventListener('ConcordServicing.Common.Events.ClientDeleted', clientChanged as EventListener);
        }
    });
</script>

<svelte:head>
    <link rel="icon" href={data.clientSettings.favIconUrl} />
</svelte:head>
<svelte:document use:theme={data.clientSettings} />

<TopBanner />

{@render children()}

<!-- Mounted once, here: a toast raised on a login-group page renders there instead of queueing until the app's Toaster mounts after login. -->
<Toaster richColors closeButton expand={true} offset={toastOffset} />
<CookieBanner />
